import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { ShipmentCheckCall } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { deleteAndValidate } from "~/shared/services/api/fetch-client.ts";

export const deleteTrackHistory = async (messageData: {
	checkCall: ShipmentCheckCall;
	poNumber: string;
}) => {
	const url = apiRoutes.PUTDELETETRACKHISTORY.replace(
		":poNumber",
		messageData.poNumber,
	).replace(":trackHistoryId", messageData.checkCall.id?.toString() ?? "");
	const resp = await deleteAndValidate(url, z.unknown());

	if (resp.success) {
		return { trackHistoryId: messageData.checkCall.id, data: resp.data };
	}

	throw new Error(resp.error);
};

export const useDeleteTrackHistory = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (messageData: {
			checkCall: ShipmentCheckCall;
			poNumber: string;
		}) => deleteTrackHistory(messageData),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Tracking history deleted successfully.");
		},
		onError: () => {
			showError("Failed to delete tracking history. Please try again.");
		},
	});

	return {
		deleteTrackHistory: mutate,
		deleteTrackHistoryAsync: mutateAsync,
		isDeletingTrackHistory: isPending,
		error,
		data,
		resetError: reset,
	};
};
